import { Inbox } from "lucide-react";

const TONES = {
  green: "bg-green-100 text-green-700",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-700",
  blue: "bg-blue-100 text-blue-700",
  slate: "bg-slate-100 text-slate-600",
};

const STATUS_TONE = {
  pending: "amber", approved: "green", rejected: "red", suspended: "red",
  confirmed: "blue", shipped: "blue", delivered: "green", cancelled: "slate",
};

export function Pill({ tone = "slate", children, className = "" }) {
  return <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium capitalize whitespace-nowrap ${TONES[tone]} ${className}`}>{children}</span>;
}

export function StatusPill({ status }) {
  return <Pill tone={STATUS_TONE[status] || "slate"}>{status || "—"}</Pill>;
}

export function Empty({ icon: Icon = Inbox, title, desc }) {
  return (
    <div className="text-center py-14 text-muted-foreground bg-white rounded-xl border border-border">
      <Icon className="h-9 w-9 mx-auto mb-2 opacity-60" />
      <p className="font-medium text-foreground">{title}</p>
      {desc && <p className="text-sm mt-0.5">{desc}</p>}
    </div>
  );
}

export function DataTable({ head, rows, empty = "No records.", testid }) {
  if (rows.length === 0) return <Empty title={empty} />;
  return (
    <div className="bg-white rounded-xl border border-border overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-muted-foreground">
          <tr>{head.map((h, i) => <th key={i} className={`p-3 font-medium whitespace-nowrap ${i === head.length - 1 && h === "Actions" ? "text-right" : ""}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border align-middle">
              {r.map((c, j) => <td key={j} className={`p-3 ${j === r.length - 1 && head[j] === "Actions" ? "text-right" : ""}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FilterTabs({ value, onChange, options }) {
  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${value === o.key ? "bg-primary text-white border-primary" : "bg-white text-muted-foreground border-border hover:border-primary/50"}`}
          data-testid={`filter-${o.key}`}
        >
          {o.label}{o.count !== undefined && <span className={`ml-1.5 text-xs ${value === o.key ? "text-white/80" : "text-muted-foreground"}`}>{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function SectionTitle({ title, desc, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
      <div>
        <h2 className="font-semibold text-lg">{title}</h2>
        {desc && <p className="text-sm text-muted-foreground mt-0.5 max-w-2xl">{desc}</p>}
      </div>
      {action}
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
export const fmtDateTime = (v) => (v ? new Date(v).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—");
